import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  type ConfirmDialogConfig,
  ConfirmationDialogComponent,
  ConfirmationDialogComponentModule,
  PageChangeEvent,
  PaginatedSearchHeaderComponentModule,
  SnackbarAlertComponent,
  SnackbarAlertComponentModule,
  VerticalExpandAnimation,
} from '@shared/ui';
import { CompatibleWSDialogComponent } from '../compatible-ws-dialog/compatible-ws-dialog.component';
import { WorkspaceCasesFacade, WorkspaceCaseListItem } from './workspace-cases.facade';
import { WorkspaceCaseData } from '../../../stores/workspace-cases.state';
import { workspaceCasesProviders } from './workspace-cases.providers';
import {
  CaseListComponent,
  CaseSortChange,
} from '../../case/list/case-list.component';

@Component({
  selector: 'ts-workspace-cases',
  templateUrl: './workspace-cases.component.html',
  styleUrls: ['./workspace-cases.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PaginatedSearchHeaderComponentModule,
    CaseListComponent,
    ConfirmationDialogComponentModule,
    SnackbarAlertComponentModule,
  ],
  providers: [...workspaceCasesProviders()],
  animations: [VerticalExpandAnimation],
})
export class WorkspaceCasesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private loadingDialog: MatDialogRef<ConfirmationDialogComponent> | null = null;
  private loadingSnackRef: MatSnackBarRef<SnackbarAlertComponent> | null = null;

  public readonly pageSizeOptions = [10, 20, 50];

  public readonly activeSort$: Observable<CaseSortChange | null>;
  public readonly nameSortDisabled$: Observable<boolean>;

  constructor(
    public readonly facade: WorkspaceCasesFacade,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly i18n: TranslateService,
    private readonly snackBar: MatSnackBar
  ) {
    this.activeSort$ = this.facade.sortCriteria$.pipe(
      map<Record<string, 'asc' | 'desc'>, CaseSortChange | null>((criteria) => {
        if (criteria['name']) return { field: 'name', direction: criteria['name'] };
        if (criteria['createdAt']) return { field: 'created', direction: criteria['createdAt'] };
        return null;
      })
    );
    this.nameSortDisabled$ = this.facade.nameEncrypted$;
  }

  ngOnInit(): void {
    this.route.parent?.paramMap
      .pipe(
        map((p) => p.get('workspaceId')),
        filter((p): p is string => !!p),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((workspaceId) => {
        this.facade.activateWorkspace(workspaceId);
      });

    // Move completion
    this.applyOperationTerminationPipe(this.facade.movingCase$)
      .pipe(withLatestFrom(this.facade.operationError$))
      .subscribe(([, error]) => {
        this.loadingDialog?.close();
        this.loadingDialog = null;
        if (!error) this.showSnack('workspaces.cases.moving.success');
      });

    // Copy completion
    this.applyOperationTerminationPipe(this.facade.copyingCase$)
      .pipe(withLatestFrom(this.facade.operationError$))
      .subscribe(([, error]) => {
        this.loadingDialog?.close();
        this.loadingDialog = null;
        if (!error) this.showSnack('workspaces.cases.copying.success');
      });

    // Delete completion
    this.applyOperationTerminationPipe(this.facade.deleting$)
      .pipe(withLatestFrom(this.facade.operationError$))
      .subscribe(([, error]) => {
        this.loadingDialog?.close();
        this.loadingDialog = null;
        if (!error) this.showSnack('workspaces.cases.deletion.success');
      });

    // Decrypt loading snack (persistent while in progress, dismiss when done)
    this.facade.decryptingCases$
      .pipe(skip(1), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((decrypting) => {
        if (decrypting) {
          this.showLoadingSnack('workspaces.cases.decryption.progress');
        } else {
          this.loadingSnackRef?.dismiss();
          this.loadingSnackRef = null;
        }
      });

    // Error snack (fires for any operation failure via operationError$)
    this.facade.operationError$
      .pipe(
        filter((e): e is string => !!e),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => {
        this.loadingDialog?.close();
        this.loadingDialog = null;
        this.showSnack(error);
      });

    // Navigation permission denied snack
    this.facade.permissionDenied$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.showSnack('workspaces.cases.navigation.permission_denied'));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchTermChange(term: string): void {
    this.facade.setSearchTerm(term);
  }

  onPageChange(event: PageChangeEvent<WorkspaceCaseData>): void {
    this.facade.loadMoreItems(event.nextToken);
  }

  onPageSizeChange(pageSize: number): void {
    this.facade.changePageSize(pageSize);
  }

  onMigrateCase(item: WorkspaceCaseListItem): void {
    this.facade.workspaceId$
      .pipe(
        take(1),
        filter((id): id is string => !!id),
        switchMap((workspaceId) =>
          this.dialog
            .open(CompatibleWSDialogComponent, {
              data: { workspaceId, acceptMessage: 'workspaces.cases.moving.action' },
              disableClose: true,
            })
            .afterClosed()
            .pipe(
              take(1),
              filter((result) => result && result !== 'cancel')
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((destinationWorkspaceId: string) => {
        this.openLoadingDialog('workspaces.cases.moving.title', 'workspaces.cases.moving.progress');
        this.facade.moveCase({ caseId: item.id, destinationWorkspaceId });
      });
  }

  onCopyCase(item: WorkspaceCaseListItem): void {
    this.facade.workspaceId$
      .pipe(
        take(1),
        filter((id): id is string => !!id),
        switchMap((workspaceId) =>
          this.dialog
            .open(CompatibleWSDialogComponent, {
              data: { workspaceId, acceptMessage: 'workspaces.cases.copying.action' },
              disableClose: true,
            })
            .afterClosed()
            .pipe(
              take(1),
              filter((result) => result && result !== 'cancel')
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((destinationWorkspaceId: string) => {
        this.openLoadingDialog('workspaces.cases.copying.title', 'workspaces.cases.copying.progress');
        this.facade.copyCase({ caseId: item.id, destinationWorkspaceId });
      });
  }

  onToggleDecryption(): void {
    this.facade.toggleDecryption();
  }

  onSortChange(event: CaseSortChange): void {
    const repositoryField = event.field === 'created' ? 'createdAt' : 'name';
    this.facade.setSortCriteria({ [repositoryField]: event.direction });
  }

  private openLoadingDialog(title: string, text: string): void {
    this.loadingDialog = this.dialog.open(ConfirmationDialogComponent, {
      data: { title, text, showLoadingSpinner: true },
      disableClose: true,
    });
  }

  private showSnack(titleKey: string): void {
    this.snackBar.openFromComponent(SnackbarAlertComponent, {
      duration: 3000,
      data: { title: titleKey, icon: 'ri-close-fill' },
    });
  }

  private showLoadingSnack(titleKey: string): void {
    this.loadingSnackRef = this.snackBar.openFromComponent(SnackbarAlertComponent, {
      data: { title: titleKey, icon: 'ri-refresh-line spin' },
    });
  }

  private applyOperationTerminationPipe(source$: Observable<boolean>): Observable<boolean> {
    return source$.pipe(
      skip(1),
      distinctUntilChanged(),
      filter((value) => !value),
      takeUntil(this.destroy$)
    );
  }

  onRemoveCase(item: WorkspaceCaseListItem): void {
    const title = this.i18n.instant('workspaces.cases.deletion.title', { name: `"${item.name}"` });
    const data: ConfirmDialogConfig = {
      title,
      text: 'dialog.cant_undo_warn',
      acceptButtonText: 'core.remove',
      cancelButtonText: 'button.cancel',
      warningDialog: true,
    };

    this.dialog
      .open(ConfirmationDialogComponent, { data, disableClose: true })
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result === 'accept'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.openLoadingDialog('workspaces.cases.deletion.deleting_title', 'workspaces.cases.deletion.progress');
        this.facade.deleteItem(item.id);
      });
  }
}
